import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { computed, DestroyRef, inject, Injectable, PLATFORM_ID, signal } from '@angular/core';
import type { LoyaltyData } from '@shared/data-access/interfaces';
import { KillswitchService } from './killswitch/killswitch.service';

type HeaderLoyaltyDataResult = Partial<LoyaltyData> | null;

@Injectable({
  providedIn: 'root',
})
export class HeaderLoyaltyService {
  private readonly document = inject(DOCUMENT);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly destroyRef = inject(DestroyRef);
  private readonly killswitchService = inject(KillswitchService);
  private readonly isHeaderLoyaltyDataEnabled = this.killswitchService.killSwitchForCurrentChain(
    'isHeaderLoyaltyDataEnabled',
  );

  private readonly LoyaltyDataRefreshEvent = 'LoyaltyDataRefresh' as const;

  private readonly _loyaltyData = signal<HeaderLoyaltyDataResult>(null);

  /*
   * Latest loyalty snapshot from the header. null only means the data is not yet available.
   * Null does not indicate authentication or loyalty status. Can update so make sure to react to changes.
   */
  readonly loyaltyData = this._loyaltyData.asReadonly();
  readonly points = computed(() => this.loyaltyData()?.points ?? null);
  readonly firstName = computed(() => this.loyaltyData()?.identity?.firstName ?? null);
  readonly tier = computed(() => this.loyaltyData()?.tier ?? null);

  constructor() {
    if (!isPlatformBrowser(this.platformId) || !this.isHeaderLoyaltyDataEnabled()) return;

    this.document.addEventListener(this.LoyaltyDataRefreshEvent, this.handleLoyaltyDataRefresh);
    this.setInitialLoyaltyData();

    this.destroyRef.onDestroy(() => {
      this.document.removeEventListener(
        this.LoyaltyDataRefreshEvent,
        this.handleLoyaltyDataRefresh,
      );
    });
  }

  private setInitialLoyaltyData(): void {
    this._loyaltyData.set(this.getLoyaltyData());
  }

  private readonly handleLoyaltyDataRefresh = (): void => {
    this._loyaltyData.set(this.getLoyaltyData());
  };

  private getLoyaltyData(): HeaderLoyaltyDataResult {
    return globalThis.navigationService?.getLoyaltyData?.() ?? null;
  }
}
